import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnsType, TableLocale } from 'antd/lib/table/interface';
import { useIntl } from 'react-intl';
import { Table as AntdTable } from 'antd';
import useQueryApiClient from 'utils/useQueryApiClient';
import {
  FilterValue,
  Key,
  SortOrder,
  TablePaginationConfig,
  TableRowSelection,
} from 'antd/es/table/interface';
import { RowClassName } from 'rc-table/lib/interface';
import { AnyObject } from 'antd/es/_util/type';
import { EditableCell } from './EditableCell';
import { Pagination } from 'types/Pagination';

export interface TableProps {
  locale?: TableLocale;
  saveData?: any;
  saveResponse?: any;
  loading?: boolean;
  options?: {};
  rowKey?: string;
  columns: ColumnsType<any> | Array<any>;
  dataSource?: object[];
  size?: 'small' | 'large' | 'middle';
  rowClassName?: string | RowClassName<AnyObject> | undefined;
  onRow?: any;
  onChange?: any;
  disablePagination?: boolean;
  components?: any;
  scroll?: any;
  linkProps?: {
    url: string;
    recordKey?: string;
  };
  url?: string;
  filter?: object;
  page_size?: number;
  defaultSort?: string;
  defaultOrder?: 'asc' | 'desc';
  enableSelectedRow?: boolean;
  reload?: number;
  setSelectedRows?: any;
  setSelectedKeys?: any;
  rowSelectionFunction?: TableRowSelection<any>;
  className?: string;
  paginnationStatus?: React.Dispatch<React.SetStateAction<Pagination>>;
  ref?: any;
  expandedRowRender?: (record: any) => React.ReactNode;
  expandedRowKeys?: React.Key[];
  onExpandedRowsChange?: (expandedKeys: readonly React.Key[]) => void;
  childrenColumnName?: string;
  indentSize?: number;
  rowExpandable?: (record: any) => boolean;
  footer?: React.ReactNode;
}

interface SorterResult {
  order?: SortOrder;
  field?: string;
  columnKey?: Key;
}

export const Table = ({
  locale,
  loading,
  rowKey = 'id',
  saveData,
  columns,
  dataSource,
  saveResponse,
  size,
  rowClassName,
  onRow,
  onChange,
  disablePagination,
  components,
  scroll,
  linkProps,
  url,
  filter,
  page_size = 25,
  defaultSort,
  defaultOrder,
  enableSelectedRow,
  reload,
  setSelectedRows,
  setSelectedKeys,
  rowSelectionFunction,
  className,
  options,
  paginnationStatus,
  ref,
  expandedRowRender,
  expandedRowKeys,
  onExpandedRowsChange,
  childrenColumnName,
  indentSize,
  rowExpandable,
  footer,
}: TableProps) => {
  const [innerData, setInnerData] = useState<any>(dataSource || []);
  const [currentPage, setCurrentPage] = useState<number | undefined>(1);
  const [sortBy, setSortBy] = useState<string | undefined>(defaultSort);
  const [orderBy, setOrderBy] = useState<string | undefined>(defaultOrder);
  const [localPagination, setLocalPagination] = useState<any>({
    showSizeChanger: false,
    showQuickJumper: false,
    position: ['bottomCenter'],
  });
  const intl = useIntl();

  useEffect(() => {
    setInnerData(dataSource);
  }, [dataSource]);

  useEffect(() => {
    setSortBy(defaultSort);
  }, [defaultSort]);

  useEffect(() => {
    setOrderBy(defaultOrder);
  }, [defaultOrder]);

  // The admin Filter component keeps page_size inside the filter form values.
  // The API reads page size only from the top-level `page_size`, so lift it out
  // of the filter payload here instead of sending it twice.
  const { page_size: filterPageSize, ...requestFilter } = (filter ?? {}) as {
    page_size?: number;
    [key: string]: unknown;
  };

  const derivedPageSize: number = useMemo(() => {
    return Number.isInteger(filterPageSize) ? Number(filterPageSize) : page_size;
  }, [filterPageSize, page_size]);

  const { refetch, isLoading } = useQueryApiClient({
    request: {
      url: url || '',
      data: {
        page: currentPage ?? 1,
        page_size: derivedPageSize,
        options: options,
        sort_by: sortBy,
        order_by: orderBy,
        filter: requestFilter,
      },
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setInnerData(disablePagination ? response : response.data);
      saveData?.(response.data);
      saveResponse?.(response);
      setCurrentPage(response.current_page);
      setLocalPagination((prev: any) => ({
        ...prev,
        lastPage: response.last_page,
        current: response.current_page,
        total: response.total,
        pageSize: response.per_page ?? derivedPageSize,
      }));
      if (currentPage !== 1 && response.data.length === 0) {
        setCurrentPage((old) => (old ? old - 1 : 1));
      }
      paginnationStatus?.({
        page: response.current_page ?? 1,
        page_size: response.per_page ?? derivedPageSize,
        total: response.total,
      });
    },
  });

  useEffect(() => {
    if (url) {
      refetch();
    }
  }, [sortBy, currentPage, orderBy, filter, derivedPageSize, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!locale) {
    locale = {
      emptyText: intl.formatMessage({ id: 'general.found_no_data' }),
    };
  }

  const basePagination = {
    showSizeChanger: false,
    showQuickJumper: true,
    defaultPageSize: derivedPageSize,
  };

  //TODO this is for sort
  const element = document.getElementsByClassName(
    'anticon anticon-down ant-select-suffix'
  );

  element[0]?.classList?.add('far');
  element[0]?.classList?.add('fa-angle-down');
  element[0]?.classList?.remove('anticon');
  element[0]?.classList?.remove('anticon-down');
  element[0]?.classList?.remove('ant-select-suffix');

  const getOnCell = useCallback(
    (col: any) => (record: object) => ({
      record,
      dataIndex: col.dataIndex,
      title: col.title,
      linkProps: linkProps,
    }),
    [linkProps]
  );

  const parsedColumns = useMemo(
    () =>
      columns.map((col: any) => {
        if (components) {
          return { ...col };
        }

        return {
          ...col,
          onCell: getOnCell(col),
        };
      }),
    [columns, components, getOnCell]
  );

  const onTableChange = async (
    { current }: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    { field, order }: SorterResult
  ) => {
    if (!order) {
      field = field || '';
      order = order || null;
    }

    setCurrentPage(current || currentPage);
    !defaultOrder && setOrderBy(order === 'descend' ? 'desc' : 'asc');
    !defaultSort && setSortBy(field as string | undefined);
  };

  const rowSelection = {
    preserveSelectedRowKeys: false,
    onChange: (selectedRowKeys: React.Key[], selectedRows: object) => {
      setSelectedRows && setSelectedRows(selectedRows);
      setSelectedKeys && setSelectedKeys(selectedRowKeys);
    },
  };

  return (
    <AntdTable
      ref={ref}
      locale={locale}
      className={className}
      loading={isLoading || loading}
      rowKey={rowKey}
      columns={parsedColumns}
      dataSource={innerData}
      size={size}
      pagination={
        !disablePagination && (localPagination.total ?? 0) > 10
          ? { ...basePagination, ...localPagination }
          : false
      }
      rowClassName={rowClassName}
      onRow={onRow}
      onChange={onChange || onTableChange}
      components={{
        body: {
          cell: EditableCell,
        },
        ...components,
      }}
      showSorterTooltip={false}
      scroll={scroll}
      rowSelection={
        enableSelectedRow ? (rowSelectionFunction ?? rowSelection) : undefined
      }
      expandable={
        expandedRowRender ||
        expandedRowKeys ||
        onExpandedRowsChange ||
        childrenColumnName ||
        rowExpandable
          ? {
              ...(expandedRowRender && { expandedRowRender }),
              ...(expandedRowKeys && { expandedRowKeys }),
              ...(onExpandedRowsChange && { onExpandedRowsChange }),
              ...(childrenColumnName && { childrenColumnName }),
              ...(rowExpandable && { rowExpandable }),
            }
          : undefined
      }
      indentSize={indentSize}
      footer={footer ? () => <div className="table-footer">{footer}</div> : undefined}
    />
  );
};
